import { t } from "@/i18n/i18n";

export interface TouchControlCallbacks {
  onMove: (x: number, y: number) => void;
  onRun: (running: boolean) => void;
  onJump: () => void;
  onAttack: () => void;
}

/** Minimal on-screen joystick + buttons for touch devices (spec section 8: "basic touch support"). */
export class TouchControls {
  private root: HTMLDivElement;
  private stickKnob: HTMLDivElement;
  private stickActive = false;
  private stickOrigin = { x: 0, y: 0 };
  private readonly maxRadius = 42;

  constructor(container: HTMLElement, private readonly callbacks: TouchControlCallbacks) {
    this.root = document.createElement("div");
    this.root.id = "touch-controls";
    this.root.innerHTML = `
      <div class="touch-stick"><div class="knob"></div></div>
      <div class="touch-btn jump">${t("touch.jump")}</div>
      <div class="touch-btn attack">${t("touch.attack")}</div>
      <div class="touch-btn run">${t("touch.run")}</div>
    `;
    container.appendChild(this.root);

    this.stickKnob = this.root.querySelector(".knob")!;
    const stick = this.root.querySelector(".touch-stick") as HTMLDivElement;
    const jumpBtn = this.root.querySelector(".jump") as HTMLDivElement;
    const attackBtn = this.root.querySelector(".attack") as HTMLDivElement;
    const runBtn = this.root.querySelector(".run") as HTMLDivElement;

    stick.addEventListener("touchstart", (e) => this.onStickStart(e), { passive: true });
    stick.addEventListener("touchmove", (e) => this.onStickMove(e), { passive: true });
    stick.addEventListener("touchend", () => this.onStickEnd());
    stick.addEventListener("touchcancel", () => this.onStickEnd());

    jumpBtn.addEventListener("touchstart", (e) => {
      e.preventDefault();
      this.callbacks.onJump();
    });
    attackBtn.addEventListener("touchstart", (e) => {
      e.preventDefault();
      this.callbacks.onAttack();
    });
    let running = false;
    runBtn.addEventListener("touchstart", (e) => {
      e.preventDefault();
      running = !running;
      runBtn.style.background = running ? "rgba(53,230,255,0.4)" : "";
      this.callbacks.onRun(running);
    });
  }

  private onStickStart(e: TouchEvent): void {
    const t = e.touches[0];
    this.stickActive = true;
    this.stickOrigin = { x: t.clientX, y: t.clientY };
  }

  private onStickMove(e: TouchEvent): void {
    if (!this.stickActive) return;
    const t = e.touches[0];
    let dx = t.clientX - this.stickOrigin.x;
    let dy = t.clientY - this.stickOrigin.y;
    const dist = Math.hypot(dx, dy);
    if (dist > this.maxRadius) {
      dx = (dx / dist) * this.maxRadius;
      dy = (dy / dist) * this.maxRadius;
    }
    this.stickKnob.style.transform = `translate(${dx - 23}px, ${dy - 23}px)`;
    this.callbacks.onMove(dx / this.maxRadius, -dy / this.maxRadius);
  }

  private onStickEnd(): void {
    this.stickActive = false;
    this.stickKnob.style.transform = "";
    this.callbacks.onMove(0, 0);
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle("hidden", !visible);
  }

  dispose(): void {
    this.root.remove();
  }
}
